import { defineStore } from "pinia";
import { useRegisterSW } from "virtual:pwa-register/vue";
import {
  showFailToast,
  showLoadingToast,
  showSuccessToast,
  ToastWrapperInstance,
} from "vant";
import { ref } from "vue";
import i18n from "@/locales";
import { clearCache } from "@/modules/common";

/**
 * 更新 Service Worker
 * 当前使用的是autoUpdate模式，即当有新版本时，会自动更新serviceWorker并刷新页面
 * 检查新版本的时机：1. 页面加载时 2. 调用useAppUpdate.checkUpdate
 */
export const useAppUpdate = defineStore("appUpdate", () => {
  const needRefresh = ref(false);
  const offlineReady = ref(false);
  let loading: ToastWrapperInstance | null = null;
  const registration = ref<null | ServiceWorkerRegistration>(null);
  useRegisterSW({
    onRegisteredSW(_url: string, reg: ServiceWorkerRegistration | undefined) {
      if (!reg) return;
      registration.value = reg;
      // 1. 注册有新版本时的回调
      registration.value?.addEventListener("updatefound", () => {
        // 1.1 之前没有安装过，不显示更新提示，页面加载期间自动安装
        if (!registration.value?.active) return;
        // 1.2 有新版本：显示更新提示
        loading = showLoadingToast(i18n.global.t("Message.updating"));
        registration.value?.installing?.addEventListener("statechange", () => {
          // 1.3 安装完成，关闭更新提示
          const worker = registration.value?.installing;
          if (!worker?.state) {
            loading?.close();
            loading = null;
          }
        });
      });
    },
    onRegisterError: () => {
      // 4. 注册失败
      loading?.close();
      loading = null;
      showSuccessToast(i18n.global.t("Message.noUpdate"));
    },
  });

  const checkUpdate = async () => {
    // 1. ServiceWorker没有注册成功，不检查更新
    if (!registration.value) {
      loading?.close();
      loading = null;
      showSuccessToast(i18n.global.t("Message.noUpdate"));
      return;
    }
    loading = showLoadingToast(i18n.global.t("Message.checkingUpdate"));
    try {
      // 2. 检查更新: autoUpdate模式下，下载资源并激活后会自动刷新页面
      await registration.value?.update();
      if (!registration.value?.installing) {
        // 3. 没有新版本
        loading.close();
        loading = null;
        showSuccessToast(i18n.global.t("Message.noUpdate"));
      }
    } catch (e) {
      // 4. 检查更新失败
      const errorMsg = (e as Error).message;
      if (errorMsg.includes("The script resource is behind a redirect")) {
        // FIXME 4.1 HACK: any-mate.com的service-worker.js被重定向了，刷新页面，follow重定向
        // 使用VPN反复横跳的情况下，会出现这个问题
        clearCache();
      } else {
        // 4.2 其他错误
        console.error(e);
        showFailToast(`${i18n.global.t("Message.updateError")}: ${errorMsg}`);
      }
    } finally {
      loading?.close();
    }
  };

  // 每小时检查一次更新
  setInterval(() => {
    registration.value?.update();
  }, 1000 * 60 * 60);

  const confirmUpdate = async () => {
    // const loading = showLoadingToast(i18n.global.t("Message.updating"));
    // try {
    //   await updateServiceWorker();
    //   needRefresh.value = false;
    //   offlineReady.value = false;
    //   showSuccessToast(i18n.global.t("Message.updatedToTheLatestVersion"));
    // } finally {
    //   loading.close();
    // }
  };

  return {
    registration,
    confirmUpdate,
    needRefresh,
    offlineReady,
    checkUpdate,
  };
});
